/// <amd-module name="Test/TestPlansHubRefresh/Pivots/ExecutePivot/Component/ExecuteTabConnectedComponent" />
import * as React from "react";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import { DynamicModuleLoader } from "redux-dynamic-modules";
import { getAllColumnFields } from "Test/TestPlansHubRefresh/Pivots/ColumnOptions/ExecuteColumnOptionUtils";
import * as ExecuteConstants from "Test/TestPlansHubRefresh/Pivots/ExecutePivot/ExecuteConstants";
import {
    IColumnAndSortOrder,
    IPointsAwareState,
    ITestPointItem,
    RunOptions
} from "Test/TestPlansHubRefresh/Pivots/ExecutePivot/Redux/PointsContracts";
import { getPointsModule } from "Test/TestPlansHubRefresh/Pivots/ExecutePivot/Redux/PointsModule";
import {
    getFilteredTestPoints,
    getTestPointsToRender,
    getUniqueFilterValuesToRender
} from "Test/TestPlansHubRefresh/Pivots/ExecutePivot/Redux/PointsSelector";
import * as ExecuteResources from "Test/TestPlansHubRefresh/Pivots/ExecutePivot/Resources";
import { getTestPointItemsComparator } from "Test/TestPlansHubRefresh/Pivots/ExecutePivot/TestPointsSortingUtility";
import { PreviewSplitterOptions, TelemetryConstants, TestHubRefreshConstants } from "Test/TestPlansHubRefresh/TestHub/Constants";
import { TelemetryActions } from "Test/TestPlansHubRefresh/TestHub/Redux//TelemetryActions";
import { SuitesActions } from "Test/TestPlansHubRefresh/TestHub/Redux/Suites/SuitesActions";
import { TestHubRefreshActions } from "Test/TestPlansHubRefresh/TestHub/Redux/TestHubRefresh/TestHubRefreshActions";
import { ITestHubRefreshLocalContext } from "Test/TestPlansHubRefresh/TestHub/Redux/TestHubRefresh/TestHubRefreshContracts";
import {
    getExecuteColumnOptionFields,
    getExecuteTabCustomColumnFieldMap
} from "Test/TestPlansHubRefresh/TestHub/Redux/TestHubRefresh/TestHubRefreshSelector";
import * as TestHubResources from "Test/TestPlansHubRefresh/TestHub/Resources";
import { Item, Items } from "Test/TestPlansHubRefresh/TestHub/Resources";
import { checkIfIsIE, compareColumnWidthsMaps, getDataProviderContent, getSelectedBuildObject } from "Test/TestPlansHubRefresh/TestHub/Util";
import { TestHubRefreshFilterCommon } from "Test/TestPlansHubRefresh/TestHub/Utils/TestHubRefreshFilterCommon";
import {
    TestHubRefreshLocalContext,
    testHubRefreshLocalContextData
} from "Test/TestPlansHubRefresh/TestHub/Utils/TestPlansHubRefreshLocalContextProvider";
import {
    ITestHubListBoxItemData,
    ITestPlansHubFilterItem,
    TestPlansHubFilterItemType
} from "Test/TestPlansHubRefresh/TestPlansHubRefreshFilter/TestPlansHubRefreshFilterComponent.Types";
import {
    IBulkMarkPointsMenu,
    IExecutePivotContextMenuParams,
    ITestRunsMenu,
    ITestRunsMenuExtended
} from "Test/TestPlansHubRefresh/TestRunsMenu/TestRunsMenu.Types";
import { ITfsPageService } from "Tfs/Platform/Page";
import { IReadonlyObservableArray, ObservableArray, ObservableCollection, ObservableValue } from "VSS/Core/Observable";
import { ScreenSize } from "VSS/Core/Util/Screen";
import { getMenuItems } from "VSS/Features/PlatformUI/ContributedMenu";
import { IVssPageContext } from "VSS/Platform/Context";
import { isFeatureEnabled, isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssComponentContext, IVssComponentProperties, IVssLayoutManager, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { ISettingsService, SettingsUserScope } from "VSS/Platform/Settings";
import { TTIMonitorActions } from "VSS/Redux/Modules/TTIMonitor";
import { Button } from "VSSUI/Button";
import { CustomCard } from "VSSUI/Card";
import { CustomHeader, HeaderTitle, TitleSize } from "VSSUI/Header";
import { CustomHeaderCommandBar } from "VSSUI/HeaderCommandBar";
import { BoltListDragEvent, cellFromEvent, IListSelection, ListDragDropBehavior, ListDragImage, ListSelection } from "VSSUI/List";
import { IListBoxItem } from "VSSUI/ListBox";
import { ContextualMenu, IMenuItem, MenuButton } from "VSSUI/Menu";
import { Observer } from "VSSUI/Observer";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { SplitButton } from "VSSUI/SplitButton";
import { Splitter, SplitterDirection } from "VSSUI/Splitter";
import { ColumnMore, ColumnSelect, ColumnSorting, ITableColumn, ITableRow, renderSimpleCell, sortItems, SortOrder, Table } from "VSSUI/Table";
import { EventDispatch, IEventDispatch } from "VSSUI/Utilities/Dispatch";
import { DragDropEffect } from "VSSUI/Utilities/DragDrop";
import { ScreenSizeObserver } from "VSSUI/Utilities/ScreenSize";
import { ZeroData } from "VSSUI/ZeroData";
import { WorkItemFormView } from "Wit/WorkItemForm/WorkItemFormView";
import { PointsActions } from "../Redux/PointsActions";
import { IPointsTableItem, TestPointsTableItemProvider } from "./TestPointsTableItemProvider";

export interface IExecuteTabComponentProps extends IVssComponentProperties {
    // test point items to be rendered in the table
    testPoints: ITestPointItem[];

    // array containing test point ids that should be rendered in table - used in conjunction with filter bar
    filteredTestPoints?: number[];

    //array containing the names of all the column option fields to be displayed
    columnOptionFields?: string[];

    // mapping of custom column reference names to display names
    customColumnFieldMap?: { [columnId: string]: string };

    // suite id for which points are rendered
    suiteId: number;

    // suite that was initially selected at page load
    initialSelectedSuiteId: number;

    // name of suite for which points are rendered
    suiteName: string;

    // id of the current plan
    planId: number;

    // name of the current plan
    planName: string;

    // areapath of the current plan
    planAreaPath: string;

    // iteration of the current plan
    planIteration: string;

    // The react context object. TestPlansHubRefreshConnectedView is the provider of this data.
    testhubRefreshContext?: ITestHubRefreshLocalContext;

    // flag indicatin loading status
    isLoading: boolean;

    // indicates if paging is in progress
    isPagingInProgress: boolean;

    // indicates whether the test case preview is enabled or not
    previewStatus: string;

    // contains values that show up in Outcome filter (like Active, Passed, Failed etc.)
    outcomeFilterValues: ObservableArray<IListBoxItem<ITestHubListBoxItemData>>;

    // contains values that show in Tester filter
    testerFilterValues: ObservableArray<IListBoxItem<ITestHubListBoxItemData>>;

    // contains values that show in Configuration filter
    configurationFilterValues: ObservableArray<IListBoxItem<ITestHubListBoxItemData>>;

    // contains values that show in AssignedTo filter
    assignedToFilterValues: ObservableArray<IListBoxItem<ITestHubListBoxItemData>>;

    // contains values that show in State filter
    stateFilterValues: ObservableArray<IListBoxItem<ITestHubListBoxItemData>>;

    // flag to denote if the user has advance license
    isAdvancedLicenseEnabled: boolean;

    // flag to denote the assign test dropdown state
    // We need this state to hide the assign tester dropdown once user has selected a tester
    assignTesterDropdownState: boolean;

    // This indicates the column name and sort order for sorting the test cases
    columnAndSortOrder: IColumnAndSortOrder;

    // map storing column id alongwith their width
    columnAndWidthMap: { [columnId: string]: number };

    // selected build for a test run
    selectedBuildId: string;

    // selected release Id for a test run
    selectedReleaseId: number;

    // selected release environment Id for a test run
    selectedReleaseEnvironmentId: number;

    // selected build definition Id for a test run
    selectedBuildDefnId: string;

    // dispatches telemetry to mark TTI
    markTTIAction(featureName: string, scenarioName: string, additionalProperties?: any): void;

    // dispatches action to load all test points
    loadAllTestPoints(planId: number, suiteId: number, continuationToken: string): void;

    //dispatches action to refresh test points on closing web runner
    refreshTestPointsForWebRunner(planId: number, suiteId: number, pointIds: number[]): void;

    // dispatches action to update the assign dropdown state. On selecting we dispatch this action with false,
    // so that the dropdown is hidden with the selection. The action is again dispatched in shouldComponentUpdate to reset the flag to true,
    // so that the assign tester dropdown opens for successive user interactions
    // We need this state to hide the assign tester dropdown once user has selected a tester
    updateAssignTesterDropdownState(state: boolean): void;

    // update column id and sort order for sorting
    updateColumnAndSortOrder(columnId: string, proposedSortOrder: SortOrder): void;

    // update the column widths in state
    changeColumnWidths(columnId: string, width: number): void;

    // show toast message notifications
    showToastNotifications(toastMessage: string, toastDurationMs: number): void;

    // add telemetry for test case results panel
    publishTelemetry(action: string, payload: { [key: string]: any }): void;
}

/**
 * ExecuteTabContentComponent: Renders the execute pivot in test plan hub
 */
export class ExecuteTabContentComponent extends VssComponent<IExecuteTabComponentProps, IVssComponentContext> {
    // ttiTelemetrySent flag is used to ensure we fire TTI telemetry event only once even in case where paged data is being loaded and component gets re-rendered many times
    private _ttiTelemetrySent: boolean;
    private _lastFocusedTestCaseId = new ObservableValue<number | undefined>(undefined);
    private _loadingWorkItem = new ObservableValue<boolean>(false);
    private _splitterSize: number | undefined;

    private _columnOptionFieldsChanged: boolean;
    private _columnWidthsChanged: boolean;

    private _testPointTableSelection: IListSelection;
    private _columnSelect: ColumnSelect;
    private _provider: TestPointsTableItemProvider;
    private _items: ObservableArray<IPointsTableItem>;

    private _eventDispatch: IEventDispatch;
    private _isSelected: ObservableValue<boolean>;

    private _asyncLoadInitiated: boolean;
    private _context: IVssPageContext;

    private _tableColumns: ITableColumn<any>[] = [];
    private _menuItemColumn: ColumnMore<any>;
    private _columnsVersionKey: number = 1;
    private _columnId: string;
    private _columnWidth: number;
    private _dragDropBehavior: ListDragDropBehavior<IPointsTableItem>;
    private _showTestCaseResultsPanel: ObservableValue<boolean>;

    private _sortingBehavior = new ColumnSorting<ITestPointItem>(
        (columnIndex: number, proposedSortOrder: SortOrder, event: React.KeyboardEvent<HTMLElement> | React.MouseEvent<HTMLElement>) => {
            if (this._tableColumns[columnIndex].sortProps && this._items.value.length > 0) {
                const sortFunction = getTestPointItemsComparator(this._tableColumns[columnIndex].id, proposedSortOrder);
                const sortFunctionsArr = sortFunction ? [sortFunction] : [null];

                /**
                 * We pass an empty array here to take care of inverting of sort order and retain the arrows in the column header when it is sorted
                 * Actual sorting is done by the custom comparator in the sorting utility
                 **/
                sortItems(columnIndex, proposedSortOrder, sortFunctionsArr, this._tableColumns, []);

                this.props.updateColumnAndSortOrder(this._tableColumns[columnIndex].id, proposedSortOrder);
            }
        }
    );

    constructor(props: IExecuteTabComponentProps, context: IVssComponentContext) {
        super(props, context);

        this._ttiTelemetrySent = false;
        this._columnOptionFieldsChanged = true;
        this._columnWidthsChanged = true;
        this._testPointTableSelection = new ListSelection(true);
        this._columnSelect = new ColumnSelect();
        this._eventDispatch = new EventDispatch();
        this._onRightClick = this._onRightClick.bind(this);
        this._eventDispatch.addEventListener("contextmenu", this._onRightClick);
        this._eventDispatch.addEventListener("click", this._setIfSelected);
        this._eventDispatch.addEventListener("keydown", this._setIfSelected);
        this._provider = new TestPointsTableItemProvider();
        this._context = context.pageContext;
        this._asyncLoadInitiated = false;
        this._isSelected = new ObservableValue<boolean>(false);
        this._showTestCaseResultsPanel = new ObservableValue<boolean>(false);

        this._menuItemColumn = new ColumnMore(
            () => {
                return {
                    id: "test-point-context-menu-column",
                    items: this._getContextualTestPointItems()
                };
            },
            undefined,
            (rowindex: number, columnIndex: number) => {
                if (!this._testPointTableSelection.selected(rowindex)) {
                    this._testPointTableSelection.select(rowindex, 1, false);
                }
            }
        );
        this._tableColumns = this._getColumns();
        this._dragDropBehavior = new ListDragDropBehavior<IPointsTableItem>({
            allowedTypes: [ExecuteConstants.TestPointDragItemType],
            id: "test-point-table",
            onDragStart: this._onDragStart,
            renderDragImage: (event) => {
                return <ListDragImage text={ExecuteConstants.DragTestPointsText} />;
            },
            type: ExecuteConstants.TestPointDragItemType
        });

        const settingsService = this.context.pageContext.getService<ISettingsService>("ISettingsService");

        const splitterSizeSettings: { [key: string]: string } | undefined = settingsService.getEntry(
            TestHubRefreshConstants.PREVIEW_SPLITTER_SIZE_KEY,
            SettingsUserScope.Me
        );

        if (splitterSizeSettings && splitterSizeSettings[TestHubRefreshConstants.SETTINGS_KEY]) {
            this._splitterSize = Number(splitterSizeSettings[TestHubRefreshConstants.SETTINGS_KEY]) ?? undefined;
        }
    }

    public shouldComponentUpdate(nextProps: IExecuteTabComponentProps) {
        this._items = this._provider.getTestPointsForTable(nextProps.testPoints, this._testPointTableSelection, nextProps.filteredTestPoints);

        if (this.props.columnOptionFields !== nextProps.columnOptionFields) {
            this._columnOptionFieldsChanged = true;
            // _columnsVersionKey is used as a unique key to force refresh the Table component and its behaviors, which were not being updated properly without it.
            // Causing a bug where columns could not be sorted immediately after modifying column options. Feedback ticket with ID 1605881.
            this._columnsVersionKey++;
        }

        if (!compareColumnWidthsMaps(this.props.columnAndWidthMap, nextProps.columnAndWidthMap)) {
            this._columnWidthsChanged = true;
            this._columnsVersionKey++;
        }
        // On Update if the dropdown state was false, we send another action to make it true.
        // This ensures the dropdown opens on successive actions to assign tester
        if (!nextProps.assignTesterDropdownState) {
            this.props.updateAssignTesterDropdownState(true);
        }

        // needed when the selected suite is changed to stop previewing the test point from previously selected suite
        if (this.props.testPoints && nextProps.testPoints && this.props.testPoints[0] && nextProps.testPoints[0]) {
            if (this.props.testPoints[0].id !== nextProps.testPoints[0].id) {
                this._lastFocusedTestCaseId.value = undefined;
            }
        } else {
            this._lastFocusedTestCaseId.value = undefined;
        }

        //when the preview is enabled and non-zero test points are loaded, select the first test point to avoid an empty preview
        if (nextProps.previewStatus === PreviewSplitterOptions.RIGHT && this._items.value.length > 0) {
            if (!this._lastFocusedTestCaseId.value) {
                this._focusFirstTestPoint(nextProps.testPoints);
            }
        }

        return true;
    }

    public render(): JSX.Element {
        let card;
        if (this.props.isLoading) {
            this._ttiTelemetrySent = false;
            card = <Spinner className="spinner-pos flex-grow" size={SpinnerSize.large} />;
        } else if (this.props.testPoints) {
            // if filteredTestPoints is valid, it's possible that testpoints length will be zero since no items match the filter. We need to render empty table in this case and not zero-data
            if (this.props.testPoints.length > 0 || this.props.filteredTestPoints) {
                // Use wrappedComponent to load the filter bar so that it can load async
                // The observer is used to disable the card buttons in case no points are selected. We change the value of the isSelected if there are no line items selected.
                // We have dispatch events on key down and key up which is fired when clicking or any keydown event on the table. Where we check the selection object to determine if any of the line
                // item is selected
                // We also have the same logic on onSelect as when there is no item selected initially, and user selects a line item , the click event is fired before the selection object changes

                let cardContainerStyle: any = {};

                // For IE we have to explicitly set the height of the div containing the table for virtualization to work.
                if (checkIfIsIE()) {
                    // 98 = 50 + 48 the height of the other sections above this one in the rhs container.
                    let height = document.getElementsByClassName("testplans-hub-refresh-rhs-container")[0].clientHeight - 98;
                    cardContainerStyle = { height: height + "px" };
                }

                /**
                 * Reload button => When you close the webrunner, webrunner uses the window.opener handle to query for this UI element in parent window and invokes a programmatic
                 * click on it, which refreshes the points status in hub refresh. See function _refreshTestPointsInParentWindow in TFS.TestManagement.TestRun.ts
                 */
                let pagingIndicationView: JSX.Element = (<></>) as JSX.Element;
                if (this.props.isPagingInProgress) {
                    // This shows three dots indicating paging
                    let view = [];
                    for (let i = 0; i < 3; i++) {
                        view.push(<span className="testplans-paging-indication" key={i} />);
                    }
                    pagingIndicationView = <>{view}</>;
                }
                card = (
                    <>
                        <WrappedComponent
                            dependencies={["ms.vss-test-web.testplans-hub-refresh-filter-component"]}
                            wrappedType={"testplans-hub-refresh-filter-component"}
                            className={"execute-pivot-filter-bar bolt-filterbar-white depth-8"}
                            filterBarItems={this._getFilterBarItems()}
                        />
                        <ScreenSizeObserver>
                            {(screenSizeProps: { screenSize: ScreenSize }) => {
                                const isSmallSize = screenSizeProps.screenSize <= ScreenSize.small;
                                return (
                                    <Observer isSelected={this._isSelected}>
                                        {(props: { isSelected: boolean }) => {
                                            const tableContent = (
                                                <div className="flex-row" style={cardContainerStyle}>
                                                    <CustomCard
                                                        className="testplans-hub-execute-card bolt-card-with-header"
                                                        headerClassName="bolt-card-header bolt-header-with-commandbar"
                                                    >
                                                        <CustomHeader>
                                                            <HeaderTitle
                                                                titleSize={TitleSize.Medium}
                                                                className="header-title-width bolt-header-title-area flex-grow scroll-hidden bolt-header-title body-xl m header-title"
                                                                ariaLevel={2}
                                                            >
                                                                {this._getCardTitle(this.props.testPoints)}
                                                                {pagingIndicationView}
                                                            </HeaderTitle>
                                                            <CustomHeaderCommandBar className="header-commandbar-width bolt-header-commandbar flex-noshrink bolt-header-commandbar-no-right-padding">
                                                                {[
                                                                    this._getBulkMarkOutcomeOptionsForHeader(props.isSelected, isSmallSize),
                                                                    this._getRunOptionsForHeader(props.isSelected, isSmallSize)
                                                                ]}
                                                            </CustomHeaderCommandBar>
                                                        </CustomHeader>
                                                        <Table
                                                            className="testplans-hub-execute-tab-testpoints-table"
                                                            containerClassName="custom-scrollbar"
                                                            columns={this._getColumns()}
                                                            eventDispatch={this._eventDispatch}
                                                            showLines={true}
                                                            scrollable={true}
                                                            itemProvider={this._items}
                                                            selection={this._testPointTableSelection}
                                                            onSelect={this._setIfSelected}
                                                            behaviors={[this._sortingBehavior, this._dragDropBehavior]}
                                                            singleClickActivation={false}
                                                            onActivate={this._openTestCaseResultsPanel}
                                                            key={"ExecuteTableVersionKey_" + this._columnsVersionKey}
                                                        />
                                                        <Button // see comment above regarding the button
                                                            text="reload"
                                                            className="invisible-reload-button"
                                                            onClick={() =>
                                                                this.props.refreshTestPointsForWebRunner(
                                                                    this.props.planId,
                                                                    this.props.suiteId,
                                                                    testHubRefreshLocalContextData.getWebRunnerPointIds()
                                                                )
                                                            }
                                                        />
                                                    </CustomCard>
                                                </div>
                                            );
                                            // the preview will be enabled only when the new boards hub is enabled and when there are non-zero test points being displayed
                                            return isFeatureEnabled(this._context, "ms.vss-work-web.new-boards-hub-feature", false) &&
                                                this.props.previewStatus === PreviewSplitterOptions.RIGHT &&
                                                this._areNonZeroPointsDisplayed() ? (
                                                <Splitter
                                                    className="scroll-hidden"
                                                    farElementClassName="flex-column"
                                                    nearElementClassName="flex-column"
                                                    initialFixedSize={this._splitterSize ?? 320}
                                                    onFixedSizeChanged={(newFixedSize: number) => {
                                                        this._onSplitterSizeChange(newFixedSize);
                                                    }}
                                                    onRenderNearElement={() => tableContent}
                                                    onRenderFarElement={() => (
                                                        <Observer
                                                            lastFocusedTestCaseId={this._lastFocusedTestCaseId}
                                                            loadingWorkItem={this._loadingWorkItem}
                                                        >
                                                            {({
                                                                lastFocusedTestCaseId,
                                                                loadingWorkItem
                                                            }: {
                                                                lastFocusedTestCaseId: number;
                                                                loadingWorkItem: boolean;
                                                            }) =>
                                                                loadingWorkItem ? (
                                                                    <Spinner className="flex-grow" size={SpinnerSize.large} />
                                                                ) : lastFocusedTestCaseId ? (
                                                                    <WorkItemFormView
                                                                        className="themed full-height"
                                                                        workItemId={lastFocusedTestCaseId}
                                                                        onSaveComplete={(workItem: any) => {
                                                                            this.props.refreshTestPointsForWebRunner(
                                                                                this.props.planId,
                                                                                this.props.suiteId,
                                                                                testHubRefreshLocalContextData.getWebRunnerPointIds()
                                                                            );
                                                                        }}
                                                                    />
                                                                ) : null
                                                            }
                                                        </Observer>
                                                    )}
                                                    splitterDirection={SplitterDirection.Vertical}
                                                />
                                            ) : (
                                                tableContent
                                            );
                                        }}
                                    </Observer>
                                );
                            }}
                        </ScreenSizeObserver>
                    </>
                );
            } else {
                const image = getAssetLocation(this.context.pageContext, ExecuteConstants.ExecuteTabZeroDataImagePath);
                card = (
                    <ZeroData
                        imagePath={image}
                        className="testplans-hub-execute-tab-zero-data"
                        imageAltText={ExecuteResources.ZeroDataImageText}
                        primaryText={ExecuteResources.ZeroDataPrimaryText}
                        secondaryText={ExecuteResources.ZeroDataSecondaryText}
                    />
                );
            }
        }

        return (
            <div className="test-plan-execute-tab">
                <DynamicModuleLoader
                    modules={[
                        getPointsModule(
                            this.props.planId,
                            this.props.suiteId,
                            this.props.testPoints,
                            this.props.initialSelectedSuiteId,
                            this.props.isShowTestsFromChildSuitesEnabled
                        )
                    ]}
                >
                    <div className="testplans-hub-execute-tab-testpoints-table-container flex-grow">{card}</div>
                </DynamicModuleLoader>
                <Observer showPanel={this._showTestCaseResultsPanel}>
                    {(props: { showPanel: boolean }) => {
                        return (
                            props.showPanel && (
                                <WrappedComponent
                                    dependencies={["ms.vss-test-web.testcase-results-panel"]}
                                    wrappedType="testcase-results-panel"
                                    wrappedProps={{
                                        showPanel: this._showTestCaseResultsPanel,
                                        testPoints: this.props.testPoints,
                                        selectedIndex: this._testPointTableSelection.value[0].beginIndex,
                                        // planId required for telemetry
                                        planId: this.props.planId,
                                        updateSelection: this._updateSelection
                                    }}
                                />
                            )
                        );
                    }}
                </Observer>
            </div>
        );
    }

    private _areNonZeroPointsDisplayed = (): boolean => {
        if (
            (this.props.testPoints && this.props.testPoints.length > 0) ||
            (this.props.filteredTestPoints && this.props.filteredTestPoints.length > 0)
        ) {
            return true;
        }
        return false;
    };

    private _onSplitterSizeChange = (value: number) => {
        this._splitterSize = value;
        const settingsService = this.context.pageContext.getService<ISettingsService>("ISettingsService");
        const entries: { [key: string]: any } = {
            [`${TestHubRefreshConstants.PREVIEW_SPLITTER_SIZE_KEY}/${TestHubRefreshConstants.SETTINGS_KEY}`]: value
        };
        settingsService.setEntries(entries, SettingsUserScope.Me);
    };

    public _getCardTitle(testPoints: ITestPointItem[]): string {
        let title = "";
        let itemString: string = Items;

        if (testPoints && testPoints.length >= 0) {
            if (testPoints.length === 1) {
                itemString = Item;
            }

            title = ExecuteResources.TestPoints + ` (${testPoints.length} ${itemString})`;
        }

        return title;
    }

    /**
     * _getFilterBarItems: Returns the filter bar items and the values to render within them in case of dropdown filter item
     */
    public _getFilterBarItems(): ITestPlansHubFilterItem[] {
        let filterBarItems: ITestPlansHubFilterItem[];

        const textFilterItem: ITestPlansHubFilterItem = {
            filterItemType: TestPlansHubFilterItemType.TextFilter,
            key: "execute-filter-text",
            filterItemKey: TestHubRefreshConstants.EXECUTE_TEXT_FILTER_ITEM_KEY,
            placeholder: ExecuteResources.TextFilterPlaceholderText,
            items: {} as ObservableArray<IListBoxItem<ITestHubListBoxItemData>>
        };

        const outcomeDropdownFilterItem: ITestPlansHubFilterItem = {
            filterItemType: TestPlansHubFilterItemType.DropDownFilter,
            key: "execute-outcome-filter",
            filterItemKey: TestHubRefreshConstants.EXECUTE_OUTCOME_FILTER_ITEM_KEY,
            placeholder: ExecuteResources.OutcomeFilterPlaceholderText,
            items: this._getDropdownItems(TestHubRefreshFilterCommon.outcomeFilterValues, this.props.outcomeFilterValues),
            selection: TestHubRefreshFilterCommon.outcomeFilterSelection,
            width: 180
        };

        const shouldAddAtMeFilterValue = true;
        const testerDropdownFilterItem: ITestPlansHubFilterItem = {
            filterItemType: TestPlansHubFilterItemType.DropDownFilter,
            key: "execute-tester-filter",
            filterItemKey: TestHubRefreshConstants.EXECUTE_TESTER_FILTER_ITEM_KEY,
            placeholder: ExecuteResources.TesterFilterPlaceholderText,
            items: this._getDropdownItems(TestHubRefreshFilterCommon.testerFilterValues, this.props.testerFilterValues, shouldAddAtMeFilterValue),
            selection: TestHubRefreshFilterCommon.testerFilterSelection,
            width: 220
        };

        const configurationDropdownFilterItem: ITestPlansHubFilterItem = {
            filterItemType: TestPlansHubFilterItemType.DropDownFilter,
            key: "execute-configuration-filter",
            filterItemKey: TestHubRefreshConstants.EXECUTE_CONFIGURATION_FILTER_ITEM_KEY,
            placeholder: ExecuteResources.ConfigurationFilterPlaceholderText,
            items: this._getDropdownItems(TestHubRefreshFilterCommon.configurationFilterValues, this.props.configurationFilterValues),
            selection: TestHubRefreshFilterCommon.configurationFilterSelection,
            width: 220
        };

        const assignedToFilterItem: ITestPlansHubFilterItem = {
            filterItemType: TestPlansHubFilterItemType.DropDownFilter,
            key: "execute-assignedto-filter",
            filterItemKey: TestHubRefreshConstants.EXECUTE_ASSIGNEDTO_FILTER_ITEM_KEY,
            placeholder: ExecuteResources.AssignedToFilterPlaceholderText,
            items: this._getDropdownItems(TestHubRefreshFilterCommon.assignedToFilterValues, this.props.assignedToFilterValues),
            selection: TestHubRefreshFilterCommon.assignedToFilterSelection,
            width: 220
        };

        const stateFilterItem: ITestPlansHubFilterItem = {
            filterItemType: TestPlansHubFilterItemType.DropDownFilter,
            key: "execute-state-filter",
            filterItemKey: TestHubRefreshConstants.EXECUTE_STATE_FILTER_ITEM_KEY,
            placeholder: ExecuteResources.StateFilterPlaceholderText,
            items: this._getDropdownItems(TestHubRefreshFilterCommon.stateFilterValues, this.props.stateFilterValues),
            selection: TestHubRefreshFilterCommon.stateFilterSelection,
            width: 160
        };

        filterBarItems = [
            textFilterItem,
            outcomeDropdownFilterItem,
            testerDropdownFilterItem,
            configurationDropdownFilterItem,
            assignedToFilterItem,
            stateFilterItem
        ];

        return filterBarItems;
    }

    public componentDidUpdate() {
        this._markTTIPerf();

        // This is done to make sure we call this only once at the start of page load.
        // This should only be run on intial load or when the tab is mounted with the initially selected testpoints.
        // For changing of suite, we dont want this to be called
        if (!this._asyncLoadInitiated && this.props.initialSelectedSuiteId === this.props.suiteId) {
            const data = getDataProviderContent(this._context);
            if (data) {
                if (data.testPointsContinuationToken) {
                    const tokenSplits: string[] = data.testPointsContinuationToken.split(";");
                    if (tokenSplits && tokenSplits.length == 2) {
                        tokenSplits[1] = data.testPointPageSize.toString();
                        data.testPointsContinuationToken = tokenSplits.join(";");
                    }
                }

                let continuationTokenToUse = "";
                if (this.props.columnOptionFields) {
                    continuationTokenToUse = "";
                } else {
                    continuationTokenToUse = data.testCasesContinuationToken;
                }

                // if the json island data is dirty then we don't want to use the continuation token since we need to reload all the data
                if (testHubRefreshLocalContextData.isJsonIslandDataDirty()) {
                    data.testPointsContinuationToken = "";
                }

                /**
                 * if json island does not contain testPoints this means the page was loaded in Define tab so we need to load all test points for this suite
                 * Or if the json island contains some test points we need to load all test points (since json island will contain only 25 points)
                 */
                if (!data.testPoints || data.testPoints.length > 0) {
                    this.props.loadAllTestPoints(this.props.planId, this.props.suiteId, continuationTokenToUse);
                }
            }
        }
        this._asyncLoadInitiated = true;
    }

    private _onDragStart = (event: BoltListDragEvent<HTMLElement, IPointsTableItem>) => {
        event.detail.dataTransfer.effectAllowed = DragDropEffect.none;
        this.props.showToastNotifications(ExecuteResources.MoveAndReorderTestCasesDisabled, TestHubRefreshConstants.TOAST_MESSAGE_DURATION);
    };

    public _openTestCaseResultsPanel = () => {
        const selectedIndex: number = this._testPointTableSelection.value[0].beginIndex;
        this._showTestCaseResultsPanel.value = true;
        const telemetryPayload = {
            testCaseId: this.props.testPoints[selectedIndex].testCaseRef.id
        };
        this.props.publishTelemetry(TelemetryConstants.OPEN_TEST_CASE_RESULTS_PANEL, telemetryPayload);
    };

    /*
    Called when test points are switched in execution history panel to reflect the change in selection in the test point table
    ** @next - true when the next test point is to be selected and false when the previous test point is to be selected
    */
    public _updateSelection = (next: boolean) => {
        const selectedIndex: number = this._testPointTableSelection.value[0].beginIndex;
        if (next) {
            if (this.props.testPoints.length === selectedIndex + 1) {
                this._testPointTableSelection.select(0);
            } else {
                this._testPointTableSelection.select(selectedIndex + 1);
            }
        } else {
            if (selectedIndex === 0) {
                this._testPointTableSelection.select(this.props.testPoints.length - 1);
            } else {
                this._testPointTableSelection.select(selectedIndex - 1);
            }
        }
    };

    private _focusFirstTestPoint = (testPoints: any) => {
        // called when the test points props are loaded inside the component and preview feature is on
        this._testPointTableSelection.select(0);

        let selectedTestPoint;
        if (testPoints) {
            selectedTestPoint = testPoints[0];
        }
        if (selectedTestPoint && selectedTestPoint.testCaseRef) {
            this._onFocusItem(selectedTestPoint.testCaseRef.id);
        }
    };

    private _setIfSelected = (event: React.MouseEvent<HTMLElement>, listRow: ITableRow<any>) => {
        const isSelected: boolean = this._testPointTableSelection.selectedCount > 0 ? true : false;

        // This check is required because we dont want the value of the observable to be changed everytime.
        if (isSelected !== this._isSelected.value) {
            this._isSelected.value = isSelected;
        }

        if (listRow) {
            const { data, index } = listRow;
            this._onFocusItem(data[TestHubRefreshConstants.WorkItemField_Id]);
        }
    };

    private _onFocusItem = async (testCaseId: number) => {
        if (this._lastFocusedTestCaseId.value !== testCaseId) {
            this._lastFocusedTestCaseId.value = testCaseId;
        }
    };

    /**
     * This method takes the class member object and the props object and merges the two and returns the dropdown filter items
     * This is required to maintain the filter context as user switches between suites. The new items in props come in as data is fetched via paging.
     * shouldAddAtMe is an optional boolean parameter used only in case of Tester filter in Execute tab, where we need to add '@me' as the default entry.
     */
    private _getDropdownItems(
        existingItems: ObservableArray<IListBoxItem<ITestHubListBoxItemData>>,
        newItems: ObservableArray<IListBoxItem<ITestHubListBoxItemData>>,
        shouldAddAtMe?: boolean
    ): ObservableArray<IListBoxItem<ITestHubListBoxItemData>> {
        if (existingItems && newItems) {
            for (const newItem of newItems.value) {
                if (existingItems.value.findIndex((value) => value.text === newItem.text) === -1) {
                    existingItems.push(newItem);
                }
            }
        }

        if (shouldAddAtMe) {
            if (existingItems.value.findIndex((value) => value.text === TestHubResources.AtMeString) === -1) {
                const defaultAtMeData = TestHubRefreshFilterCommon.getDefaultAtMeTesterData(this._context);

                if (defaultAtMeData) {
                    const defaultAtMeFilterValue: IListBoxItem<ITestHubListBoxItemData> = {
                        id: defaultAtMeData.value.displayName,
                        text: TestHubResources.AtMeString,
                        data: defaultAtMeData
                    };

                    // add '@me' to the top of the tester dropdown items list
                    existingItems.splice(0, 0, defaultAtMeFilterValue);
                }
            }
        }

        return existingItems;
    }

    private _getRunMenuDetails(): ITestRunsMenuExtended {
        const testRunParam: ITestRunsMenuExtended = {
            selection: this._testPointTableSelection,
            points: this.props.testPoints,
            planId: this.props.planId,
            planName: this.props.planName,
            suiteId: this.props.suiteId,
            suiteName: this.props.suiteName,
            planIteration: this.props.planIteration,
            planAreaPath: this.props.planAreaPath,
            dispatch: this.props.dispatch,
            selectedBuildId: this.props.selectedBuildId,
            selectedReleaseId: this.props.selectedReleaseId,
            selectedReleaseEnvironmentId: this.props.selectedReleaseEnvironmentId,
            selectedBuildDefnId: this.props.selectedBuildDefnId
        };
        return testRunParam;
    }

    private _getBulkMarkMenuDetails(): IBulkMarkPointsMenu {
        const bulkMarkParam: IBulkMarkPointsMenu = {
            selection: this._testPointTableSelection,
            points: this.props.testPoints,
            planId: this.props.planId,
            suiteId: this.props.suiteId,
            dispatch: this.props.dispatch,
            isAdvancedLicenseEnabled: this.props.isAdvancedLicenseEnabled
        };
        return bulkMarkParam;
    }

    // Get Contextual menu items for bulk mark points
    private _getSubMenuBulkMarkItems = (): IReadonlyObservableArray<IMenuItem> => {
        const menuItems: ObservableCollection<IMenuItem> = new ObservableCollection();
        menuItems.push(getMenuItems(this.context.pageContext, "ms.vss-test-web.bulkmark-options-menu", this._getBulkMarkMenuDetails()));
        return menuItems;
    };

    // Get Contextual menu items for the run menu
    private _getSubMenuItemsForRunOptions = (): IReadonlyObservableArray<IMenuItem> => {
        const menuItems: ObservableCollection<IMenuItem> = new ObservableCollection();
        menuItems.push(getMenuItems(this.context.pageContext, "ms.vss-test-web.points-execute-options-menu", this._getRunMenuDetails()));
        return menuItems;
    };

    private _getBulkMarkOutcomeOptionsForHeader(isSelected: boolean, isSmallSized: boolean): JSX.Element {
        return (
            <MenuButton
                key={"execute-header-bulk-mark-menu-primary-split-button"}
                iconProps={{ iconName: "CompletedSolid" }}
                disabled={!isSelected}
                className={
                    isSelected
                        ? "testplans-execute-menu bolt-header-command-item-button " +
                          (isSmallSized ? "command-item-button-width-s" : "command-item-button-width")
                        : "testplans-execute-menu-disabled bolt-header-command-item-button " +
                          (isSmallSized ? "command-item-button-width-s" : "command-item-button-width")
                }
                ariaLabel={ExecuteResources.BulkMarkTestPoints}
                contextualMenuProps={{
                    menuProps: {
                        id: "subMenu",
                        items: this._getSubMenuBulkMarkItems()
                    }
                }}
            />
        );
    }

    private _getRunOptionsForHeader(isSelected: boolean, isSmallSized: boolean) {
        return (
            <SplitButton
                key={"execute-header-run-menu-primary-split-button"}
                disabled={!isSelected}
                buttonProps={{
                    text: ExecuteResources.RunForWebApplication,
                    className: isSmallSized ? "run-button-width-s" : "run-button-width",
                    tooltipProps: isSmallSized ? { text: ExecuteResources.RunForWebApplication } : {},
                    onClick: () => this._launchWebRunner()
                }}
                className="split-button-width"
                menuButtonProps={{
                    ariaLabel: ExecuteResources.SeeRunTestOptions,
                    className: "menu-button-width",
                    contextualMenuProps: {
                        menuProps: {
                            id: "subMenu2",
                            items: this._getSubMenuItemsForRunOptions()
                        }
                    }
                }}
            />
        );
    }

    private _launchWebRunner() {
        const selectedtestPoints: ITestPointItem[] = [];
        const testPointIds: number[] = [];
        const selectionLength = this._testPointTableSelection.value.length;
        for (let i = 0; i < selectionLength; i++) {
            for (let j = this._testPointTableSelection.value[i].beginIndex; j <= this._testPointTableSelection.value[i].endIndex; j++) {
                if (this.props.testPoints[j]) {
                    selectedtestPoints.push(this.props.testPoints[j]);
                    testPointIds.push(this.props.testPoints[j].id);
                }
            }
        }

        const runParams: ITestRunsMenu = {
            planAreaPath: this.props.planAreaPath,
            planId: this.props.planId,
            planIteration: this.props.planIteration,
            planName: this.props.planName,
            points: selectedtestPoints,
            suiteId: this.props.suiteId,
            suiteName: this.props.suiteName,
            runOptions: RunOptions.RunTestUsingWebRunner,
            selectedBuild: getSelectedBuildObject(this.props.selectedBuildId)
        };

        /**
         * Point Ids which are invoked in web runner are stored in local context
         * this is done to refresh points in UI when web runner is closed.
         */
        testHubRefreshLocalContextData.setWebRunnerPointIds(testPointIds);

        this.props.openTestPointsRunnerClient(runParams);
    }

    private _markTTIPerf() {
        if (this.props.testPoints && this.props.testhubRefreshContext) {
            if (this.props.testhubRefreshContext.isPageLoad()) {
                // This is a page load scenario, use helper action to declare TTI
                this.props.markTTIAction(TelemetryConstants.TEST_HUB_FEATURE_AREA_NAME, TelemetryConstants.EXECUTE_PIVOT_PAGE_LOAD_TTI_SCENARIO, {
                    ttiScenario: "PageLoad"
                });
                this.props.testhubRefreshContext.setPageLoad(false);
                this._ttiTelemetrySent = true;
            } else {
                if (this.props.isLoading === false && this._ttiTelemetrySent === false) {
                    // This is either a tab switch (from define -> execute for e.g.) or a suite selection changed scenario
                    const performanceSerivce: IVssPerformanceService =
                        this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");

                    // declare TTI only if the scenario was already started (i.e. it is currently active). For suite change, the scenario would have been started in Suite connected component
                    if (performanceSerivce.getActiveScenario(TelemetryConstants.EXECUTE_PIVOT_PAGE_LOAD_TTI_SCENARIO)) {
                        performanceSerivce.endScenario(
                            TelemetryConstants.TEST_HUB_FEATURE_AREA_NAME,
                            TelemetryConstants.EXECUTE_PIVOT_PAGE_LOAD_TTI_SCENARIO,
                            true
                        );
                        this._ttiTelemetrySent = true;
                    }
                }
            }
        }
    }

    private _getContextualTestPointItems = (): IReadonlyObservableArray<IMenuItem> => {
        const menuItems: ObservableCollection<IMenuItem> = new ObservableCollection();
        const props: IExecutePivotContextMenuParams = {
            selection: this._testPointTableSelection,
            planId: this.props.planId,
            planName: this.props.planName,
            planAreaPath: this.props.planAreaPath,
            planIteration: this.props.planIteration,
            suiteId: this.props.suiteId,
            suiteName: this.props.suiteName,
            points: this.props.testPoints,
            dispatch: this.props.dispatch,
            isAdvancedLicenseEnabled: this.props.isAdvancedLicenseEnabled,
            selectedBuild: getSelectedBuildObject(this.props.selectedBuildId),
            selectedBuildId: this.props.selectedBuildId,
            selectedReleaseId: this.props.selectedReleaseId,
            selectedReleaseEnvironmentId: this.props.selectedReleaseEnvironmentId,
            selectedBuildDefnId: this.props.selectedBuildDefnId,
            openTestCaseResultsPanel: this._openTestCaseResultsPanel
        };

        menuItems.push(getMenuItems(this.context.pageContext, "ms.vss-test-web.test-point-context-menu", props));

        if (isFeatureFlagEnabled(this.context.pageContext, TestHubRefreshConstants.EnableThirdPartyMenuContributionsFF, false)) {
            // contextual data required by menu extensions
            const extensionContext: any = [];
            const selectionLength = this._testPointTableSelection.value.length;
            const projectName: string = this._getProjectName();
            for (let i = 0; i < selectionLength; i++) {
                for (let j = this._testPointTableSelection.value[i].beginIndex; j <= this._testPointTableSelection.value[i].endIndex; j++) {
                    if (this.props.testPoints[j]) {
                        const testPointContext = {
                            suiteId: this.props.suiteId,
                            suiteName: this.props.suiteName,
                            testCaseId: this.props.testPoints[j].testCaseRef?.id,
                            testPointId: this.props.testPoints[j].id,
                            automated: this.props.testPoints[j].isAutomated,
                            build: this.props.selectedBuildId,
                            configurationId: this.props.testPoints[j].configuration?.id,
                            configurationName: this.props.testPoints[j].configuration?.name,
                            mostRecentResultId: this.props.testPoints[j].lastResultId,
                            mostRecentRunId: this.props.testPoints[j].lastRunId,
                            outcome: this.props.testPoints[j].outcomeItem?.outcomeString,
                            sequenceNumber: this.props.testPoints[j].order,
                            state: this.props.testPoints[j].state,
                            assignedTo: this.props.testPoints[j].testCaseRef?.assignedTo?.displayName,
                            tester: this.props.testPoints[j].tester?.id,
                            "System.AssignedTo": this.props.testPoints[j].testCaseRef?.assignedTo?.displayName,
                            "System.Id": this.props.testPoints[j].testCaseRef?.id,
                            "System.State": this.props.testPoints[j].testCaseRef?.state,
                            "System.Title": this.props.testPoints[j].testCaseRef?.title,
                            "System.WorkItemType": ExecuteConstants.TestCaseWorkItemType,
                            "System.TeamProject": projectName
                        };
                        extensionContext.push(testPointContext);
                    }
                }
            }

            //adding 3rd party menu which are targetting id - test-run-grid-menu
            menuItems.push(getMenuItems(this.context.pageContext, "ms.vss-test-web.test-run-grid-menu", extensionContext));
        }
        return menuItems;
    };

    private _onColumnSize = (event: MouseEvent, columnIndex: number, width: number) => {
        const originalColumnWidth = this._tableColumns[columnIndex].width;
        (originalColumnWidth as ObservableValue<number>).value = width;
        this._columnId = this._tableColumns[columnIndex].id;
        this._columnWidth = width;
    };

    private _columnSizeEnd = () => {
        this.props.changeColumnWidths(this._columnId, this._columnWidth);
        this.props.saveColumnWidths(this.props.columnAndWidthMap);
    };

    private _getColumns(): ITableColumn<any>[] {
        if (this._tableColumns.length === 0 || this._columnOptionFieldsChanged || this._columnWidthsChanged) {
            this._columnWidthsChanged = false;
            this._tableColumns = [];
            this._tableColumns.push(this._columnSelect, {
                id: ExecuteConstants.TitleColumn,
                name: ExecuteResources.Title,
                width: this._getWidth(480, ExecuteConstants.TitleColumn, this.props.columnAndWidthMap),
                minWidth: 10,
                onSize: this._onColumnSize,
                onSizeEnd: this._columnSizeEnd,
                renderCell: renderSimpleCell,
                className: "body-m",
                sortProps:
                    this.props.columnAndSortOrder && this.props.columnAndSortOrder.columnId === ExecuteConstants.TitleColumn
                        ? { sortOrder: this.props.columnAndSortOrder.sortOrder }
                        : {}
            });

            this._tableColumns.push(this._menuItemColumn);

            this._tableColumns.push(
                {
                    id: ExecuteConstants.OutcomeColumn,
                    name: ExecuteResources.Outcome,
                    width: this._getWidth(130, ExecuteConstants.OutcomeColumn, this.props.columnAndWidthMap),
                    minWidth: 10,
                    onSize: this._onColumnSize,
                    onSizeEnd: this._columnSizeEnd,
                    renderCell: renderSimpleCell,
                    className: "body-m",
                    sortProps:
                        this.props.columnAndSortOrder && this.props.columnAndSortOrder.columnId === ExecuteConstants.OutcomeColumn
                            ? { sortOrder: this.props.columnAndSortOrder.sortOrder }
                            : {}
                },
                {
                    id: ExecuteConstants.OrderColumn,
                    name: ExecuteResources.Order,
                    width: this._getWidth(80, ExecuteConstants.OrderColumn, this.props.columnAndWidthMap),
                    minWidth: 10,
                    onSize: this._onColumnSize,
                    onSizeEnd: this._columnSizeEnd,
                    renderCell: renderSimpleCell,
                    className: "body-m",
                    sortProps:
                        this.props.columnAndSortOrder && this.props.columnAndSortOrder.columnId === ExecuteConstants.OrderColumn
                            ? { sortOrder: this.props.columnAndSortOrder.sortOrder }
                            : {}
                }
            );

            if (this.props.columnOptionFields) {
                this._columnOptionFieldsChanged = false;
                const fields = getAllColumnFields(this.props.columnOptionFields, this.props.customColumnFieldMap);
                for (const columnOptionField of fields) {
                    this._tableColumns.push({
                        id: columnOptionField.id,
                        name: columnOptionField.name,
                        width: this._getWidth(100, columnOptionField.id, this.props.columnAndWidthMap),
                        minWidth: 10,
                        onSize: this._onColumnSize,
                        onSizeEnd: this._columnSizeEnd,
                        renderCell: renderSimpleCell,
                        className: "body-m",
                        sortProps:
                            this.props.columnAndSortOrder && this.props.columnAndSortOrder.columnId === columnOptionField.id
                                ? { sortOrder: this.props.columnAndSortOrder.sortOrder }
                                : {}
                    });
                }
            }
        } else {
            // Render only if the tester dropdown is set. This is always set to true. Only
            // when a tester is assigned ,it is set to false and then resets back to true.
            // This is required so that the assign tester dropdown and contextual menu gets removed on selecting a tester
            const contextMenuIndex: number = this._tableColumns.indexOf(this._menuItemColumn);

            if (this.props.assignTesterDropdownState) {
                if (contextMenuIndex === -1) {
                    // insert the context menu at index 2
                    this._tableColumns.splice(2, 0, this._menuItemColumn);
                }
            } else {
                // remove the context menu if tester dropdownState is not set
                this._tableColumns.splice(contextMenuIndex, 1);
            }
        }
        return this._tableColumns;
    }

    private _getWidth(
        defaultWidth: number,
        columnId: string,
        columnAndWidthMap: { [columnId: string]: number } | undefined
    ): ObservableValue<number> {
        return columnAndWidthMap && columnAndWidthMap[columnId] !== undefined
            ? new ObservableValue(columnAndWidthMap[columnId])
            : new ObservableValue(defaultWidth);
    }

    private _onRightClick(event: React.MouseEvent<HTMLElement>) {
        if (event.button === TestHubRefreshConstants.RIGHT_MOUSE_BUTTON) {
            event.preventDefault();

            const { cellElement, rowIndex } = cellFromEvent(event);

            if (rowIndex >= 0) {
                if (!this._testPointTableSelection.selected(rowIndex)) {
                    this._testPointTableSelection.select(rowIndex, 1, false);
                }

                const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
                layoutManager.renderCallout((onDismiss) => {
                    return React.createElement(
                        ContextualMenu,
                        {
                            menuProps: {
                                id: "test-point-context-menu-column",
                                className: "test-point-context-menu",
                                items: this._getContextualTestPointItems()
                            },
                            anchorElement: event.currentTarget as HTMLElement,
                            anchorPoint: { x: event.pageX, y: event.pageY },
                            onDismiss: onDismiss
                        },
                        null
                    );
                });
            }
        }
    }

    private _getProjectName(): string {
        const tfsPageService = this._context.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();
        let projectName = "";
        if (!tfsPageData || !tfsPageData.project) {
            throw new Error("Project missing");
        } else {
            projectName = tfsPageData.project.name;
        }
        return projectName;
    }
}

function mapStateToProps(state: IPointsAwareState): Partial<IExecuteTabComponentProps> {
    let testPoints;
    let filteredTestPoints;
    let columnOptionFields;
    let assignTesterDropdownState;
    let outcomeFilterValues, testerFilterValues, configurationFilterValues, assignedToFilterValues, stateFilterValues;
    let customColumnFieldMap;

    if (state.executeState && state.executeState.testPoints) {
        testPoints = getTestPointsToRender(state);
        filteredTestPoints = getFilteredTestPoints(state);
        columnOptionFields = getExecuteColumnOptionFields(state);
        customColumnFieldMap = getExecuteTabCustomColumnFieldMap(state);

        const filterValuesWrapper = getUniqueFilterValuesToRender(state);
        outcomeFilterValues = filterValuesWrapper[TestHubRefreshConstants.EXECUTE_OUTCOME_FILTER_ITEM_KEY];
        testerFilterValues = filterValuesWrapper[TestHubRefreshConstants.EXECUTE_TESTER_FILTER_ITEM_KEY];
        configurationFilterValues = filterValuesWrapper[TestHubRefreshConstants.EXECUTE_CONFIGURATION_FILTER_ITEM_KEY];
        assignedToFilterValues = filterValuesWrapper[TestHubRefreshConstants.EXECUTE_ASSIGNEDTO_FILTER_ITEM_KEY];
        stateFilterValues = filterValuesWrapper[TestHubRefreshConstants.EXECUTE_STATE_FILTER_ITEM_KEY];

        assignTesterDropdownState = state.executeState.assignTesterDropDownState;
    }

    return {
        testPoints: testPoints,
        filteredTestPoints: filteredTestPoints,
        columnOptionFields: columnOptionFields,
        customColumnFieldMap: customColumnFieldMap,
        suiteId: state.testHubRefreshState.selectedSuiteId,
        suiteName: state.testHubRefreshState.selectedSuiteName,
        initialSelectedSuiteId: state.testHubRefreshState.initialSelectedSuiteId,
        planId: state.testHubRefreshState.testPlanId,
        planName: state.testHubRefreshState.testPlanName,
        planAreaPath: state.testHubRefreshState.testPlanAreaPath,
        planIteration: state.testHubRefreshState.testPlanIteration,
        isLoading: state.testHubRefreshState.isLoading,
        isAdvancedLicenseEnabled: state.testHubRefreshState.isAdvancedExtensionEnabled,
        isShowTestsFromChildSuitesEnabled: state.testHubRefreshState.isShowTestsFromChildSuitesEnabled,

        outcomeFilterValues: outcomeFilterValues,
        testerFilterValues: testerFilterValues,
        configurationFilterValues: configurationFilterValues,
        assignedToFilterValues: assignedToFilterValues,
        stateFilterValues: stateFilterValues,

        assignTesterDropdownState: assignTesterDropdownState,
        isPagingInProgress: state.testHubRefreshState.isPagingInProgress,
        columnAndSortOrder: state.executeState && state.executeState.columnAndSortOrder ? state.executeState.columnAndSortOrder : undefined,
        columnAndWidthMap: state.testHubRefreshState.executeColumnAndWidthMap,
        selectedBuildId: state.testHubRefreshState.selectedBuildId,
        selectedReleaseId: state.testHubRefreshState.selectedReleaseId,
        selectedReleaseEnvironmentId: state.testHubRefreshState.selectedReleaseEnvironmentId,
        selectedBuildDefnId: state.testHubRefreshState.selectedBuildDefnId,
        previewStatus: state.testHubRefreshState.testCasePreviewStatus
    };
}

function mapDispatchToProps(dispatch: Dispatch) {
    return {
        markTTIAction: (featureName: string, scenarioName: string, additionalProperties?: any) =>
            dispatch(TTIMonitorActions.markTTI(featureName, scenarioName, additionalProperties)),
        loadAllTestPoints: (planId: number, suiteId: number, continuationToken: string) =>
            dispatch(PointsActions.loadTestPointsForSelectedSuite(planId, suiteId, continuationToken)),
        refreshTestPointsForWebRunner: (planId: number, suiteId: number, pointIds: number[]) =>
            dispatch(PointsActions.refreshTestPointsForWebRunner(planId, suiteId, pointIds)),
        updateAssignTesterDropdownState: (state: boolean) => dispatch(PointsActions.updateAssignTesterDropdownState(state)),
        openTestPointsRunnerClient: (params: ITestRunsMenuExtended) => dispatch(PointsActions.openTestPointsRunnerClient(params)),
        updateColumnAndSortOrder: (columnId: string, proposedSortOrder: SortOrder) =>
            dispatch(PointsActions.updateColumnAndSortOrder(columnId, proposedSortOrder)),
        changeColumnWidths: (columnId: string, width: number) =>
            dispatch(TestHubRefreshActions.changeExecuteColumnWidths(columnId, width, undefined)),
        saveColumnWidths: (columnAndWidthMap: { [columnId: string]: number }) => dispatch(PointsActions.saveColumnWidths(columnAndWidthMap)),
        showToastNotifications: (toastMessage: string, toastDurationMs: number) =>
            dispatch(SuitesActions.showToastNotifications(toastMessage, toastDurationMs)),
        publishTelemetry: (action: string, payload: { [key: string]: any }) => dispatch(TelemetryActions.publishTelemetry(action, payload)),
        dispatch
    };
}

/**
 * ExecuteTabFunctionalComponent: This functional component is just a dumb wrapper over ExecuteTabContentComponent.
 * We use this because React.useContext can only be invoked in function components. We get the context here and then pass is as props to the class component.
 */
export function ExecuteTabFunctionalComponent(props: IExecuteTabComponentProps): JSX.Element {
    const testhubRefreshLocalContext = React.useContext(TestHubRefreshLocalContext);

    return <ExecuteTabContentComponent {...props} testhubRefreshContext={testhubRefreshLocalContext} />;
}

export const ExecuteTabConnectedComponent = connect(mapStateToProps, mapDispatchToProps)(ExecuteTabFunctionalComponent);

VssComponent.register("executePivot", ExecuteTabConnectedComponent as ((props: IExecuteTabComponentProps) => JSX.Element));
